import { useState } from 'react';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { toast } from 'react-toastify';
import { Accordion } from 'components/DesignSystems/Accordion/Accordion';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import { useUpsertLayerNodeAssignmentMutation } from 'hooks/layerNode/useUpsertLayerNodeAssignmentMutation';
import type { LayerGroupDto } from 'hooks/layerNode/types';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { LayerMetadata } from './LayerDataRows';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';

type ViewLayerCategoryModalProps = {
  show: boolean;
  onClose: () => void;
  folderGroup: LayerGroupDto | null;
  allGroups: LayerGroupDto[];
};

function isExternalLayer(
  layer: ExternalLayer | InternalLayer
): layer is ExternalLayer {
  return (
    'arcgisItemId' in layer || 'serviceUrl' in layer || 'itemType' in layer
  );
}

export function ViewLayerFolderModal({
  show,
  onClose,
  folderGroup,
  allGroups,
}: ViewLayerCategoryModalProps) {
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const upsertLayerNodeAssignment = useUpsertLayerNodeAssignmentMutation();

  if (!show || !folderGroup) {
    return null;
  }

  const { folder, items } = folderGroup;
  const folderName = folder?.name ?? 'Uncategorized';

  // Layers that are NOT already in the current folder.
  const currentLayerIds = new Set(items.map((item) => item.layer.id));
  const availableLayers = allGroups
    .flatMap((g) => g.items)
    .filter((item) => !currentLayerIds.has(item.layer.id));

  const handleSubmit = async () => {
    if (!selectedLayerId) return;

    const selectedItem = availableLayers.find(
      (item) => item.layer.id === selectedLayerId
    );
    if (!selectedItem) return;

    const { layer } = selectedItem;

    await upsertLayerNodeAssignment.mutateAsync({
      kind: isExternalLayer(layer) ? 'external' : 'internal',
      id: layer.id,
      groupGuid: layer.groupGuid,
      targetFolderId: folder ? folder.id : null,
    });
    toast.success('Layer category updated');
    setSelectedLayerId(null);
    onClose();
  };

  return (
    <CustomModal
      show={show}
      heading={folderName}
      onClose={onClose}
      onConfirm={handleSubmit}
      saveButton="Submit"
      buttonDisable={!selectedLayerId || upsertLayerNodeAssignment.isPending}
    >
      <div className="d-grid gap-5">
        <div>
          <p className="fw-semibold mb-2">Layers</p>
          {items.length === 0 ? (
            <p className="text-muted small mb-0">No layers in this folder</p>
          ) : (
            <div className="d-grid gap-2">
              {items.map((item) => (
                <Accordion
                  key={item.layer.id}
                  eventKey={item.layer.id}
                  header={
                    <span className="fw-semibold">{item.layer.name}</span>
                  }
                  showIcon
                  classes={{ card: 'border border-neutral-500' }}
                >
                  <div className="d-grid gap-2 p-2">
                    <LayerMetadata
                      lastUpdated={formatDateTime(item.layer.updatedAt)}
                      aorIds={item.layer.aorIds}
                      incident={item.layer.incidentId}
                      sources={
                        isExternalLayer(item.layer) ? 'ArcGIS Online' : 'PRATUS'
                      }
                    />
                  </div>
                </Accordion>
              ))}
            </div>
          )}
        </div>

        <PratusSelect
          label="Move layer to category"
          options={availableLayers.map((item) => ({
            value: item.layer.id,
            label: item.layer.name,
          }))}
          value={
            selectedLayerId
              ? {
                  value: selectedLayerId,
                  label:
                    availableLayers.find(
                      (item) => item.layer.id === selectedLayerId
                    )?.layer.name ?? '',
                }
              : undefined
          }
          onChange={(option) => {
            if (!option) {
              setSelectedLayerId(null);
              return;
            }
            const opt = option as { value: string };
            setSelectedLayerId(opt.value);
          }}
          isClearable
        />
      </div>
    </CustomModal>
  );
}
